/**
 * ECommerce payment step
 *
 * @author Pablo <pablo@gestionaleauto.com>
 */

/**
 * Main
 */
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import HttpStatus from 'http-status-codes';
/**
 * Material UI
 */
import { Box, Button } from '@mui/material';
import { makeStyles } from '@mui/styles';
/**
 * Paypal
 */
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
/**
 * Redux
 */
import { useDispatch, useSelector } from 'react-redux';
import { lookupsState } from '../../redux/lookupsSlice';
import { failUpdate, setStep, wizardState } from '../../redux/wizardSlice';
import { leadState } from '../../redux/leadSlice';

/**
 * Components
 */
import VehicleName from '../../components/VehicleName';
import Api from '../../helpers/api';
import { steps } from '../../helpers/const';
import { getToken } from '../../helpers/auth';

const useStyles = makeStyles((theme) => ({
  box: {
    padding: 20,
    borderRadius: 16,
    marginTop: 20,
    backgroundColor: theme.palette.secondary.main,
    [theme.breakpoints.up('md')]: {
      marginLeft: 10,
    },
  },
  logoPaypal: {
    maxWidth: 172,
  },
  navigationButtons: {
    display: 'flex',
    width: '100%',
    flexDirection: 'column',
    rowGap: 16,
    [theme.breakpoints.up('md')]: {
      justifyContent: 'center',
      flexDirection: 'row',
      rowGap: 0,
      columnGap: 16,
      margin: '40px 0px',
    },
  },
  payPalAdvance: {
    color: theme.palette.primary.main,
  },
}));

/**
 * @returns {JSX.Element}
 */
function PaypalStep() {
  const classes = useStyles();
  const { t } = useTranslation();
  const { configuration } = useSelector(lookupsState);
  const { step } = useSelector(wizardState);
  const { leadId } = useSelector(leadState);
  const dispatch = useDispatch();
  const payPalAdvance = configuration.paypal.amount;
  const activePaypal = configuration?.financial?.paypal === 'true';
  const paypalOptions = {
    'client-id': configuration.financial.paypal_client_id,
    currency: 'EUR',
  };
  const updatePaymentStatus = async (metadata, action = 'payment') => {
    const res = await Api.patch({
      entryPoint: `/lead/${leadId}/paypal/${action}`,
      headers: { 'ga-token': getToken(leadId) },
      data: metadata,
    });
    if (res.status !== HttpStatus.OK) {
      dispatch(failUpdate());
    }
    if (res.data.success && action === 'payment') {
      dispatch(setStep(steps.feedback));
    }
  };
  useEffect(() => {
    if (!activePaypal) {
      dispatch(setStep(step + 1));
    }
  }, []);
  if (!activePaypal) {
    return <div />;
  }
  return (
    <PayPalScriptProvider options={paypalOptions}>
      <div className="ga-ecommerce-paypal-wrapper col-xs-12">
        <div className={classes.box}>
          <Box mt={5}>
            <img alt="PayPal Logo" className={classes.logoPaypal} src="/wp-content/themes/gestionaleauto/img/common/paypal-full.png" />
          </Box>
          <Box mt={4} mb={5} px={5} className="ga-ecommerce-paypal-text">
            {`${t('We guarantee you priority on this vehicle by paying a small advance')}.`}
            {`${t('After viewing the car you can decide whether to proceed with the purchase at the dealership')}.`}
          </Box>
          <VehicleName />
          <div className={`ga-ecommerce-paypal-price ${classes.payPalAdvance}`} style={{ fontSize: '1.4em', fontWeight: 'bold' }}>
            {`${payPalAdvance} €`}
          </div>
          <div className={classes.navigationButtons}>
            <div>
              <Button
                variant="outlined"
                onClick={() => dispatch(setStep(steps.feedback))}
                className="ga-ecommerce-paypal-no-thanks-button"
              >
                {t('No thanks')}
              </Button>
            </div>
            <PayPalButtons
              style={{ layout: 'horizontal', height: 33 }}
              onApprove={(data, actions) => actions.order.capture().then((details) => {
                updatePaymentStatus(details, 'payment');
              })}
              createOrder={(data, actions) => {
                updatePaymentStatus(data, 'booking');
                return actions.order.create({
                  purchase_units: [
                    {
                      custom_id: leadId,
                      amount: {
                        value: payPalAdvance,
                      },
                    },
                  ],
                });
              }}
            />
          </div>
        </div>
      </div>
    </PayPalScriptProvider>
  );
}

export default PaypalStep;
